import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  YesOrNoArtifact,
  DesiredAction,
  DesiredActionKey,
  DesiredActionResponse,
  AnalyzeTodoClarifyingQuestion,
} from '@dreamai/types';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useRadiusCheck } from '../../../hooks/useRadiusCheck';
import { ReactComponent as TickSelectIcon } from '../../../icons/tick-select.svg';
import UniNav from '../UniNav';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as SpeechBubbleIcon } from '../../../icons/speech-bubble.svg';
import { ReactComponent as MinusCircleIcon } from '../../../icons/minus-circle.svg';
import { ReactComponent as ActionBuyIcon } from '../../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../../icons/action-research.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as ActionBuyAndActIcon } from '../../../icons/action-buy-and-act.svg';
import { ReactComponent as ActionCalendarInviteIcon } from '../../../icons/action-calendar-invite.svg';
import { ReactComponent as ActionBringAndBuyIcon } from '../../../icons/action-bring-and-buy.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ActionPhoneIcon } from '../../../icons/action-phone.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../../icons/action-fill-in.svg';
import { ReactComponent as ActionReserveIcon } from '../../../icons/action-reserve.svg';
import { ReactComponent as ActionActIcon } from '../../../icons/action-act.svg';
import { ReactComponent as ActionOtherIcon } from '../../../icons/action-other.svg';
import { ReactComponent as TransportItineraryIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as ActionProjectIcon } from '../../../icons/action-project.svg';
import { ReactComponent as MagicWandIcon } from '../../../icons/magic-wand.svg';
import { ReactComponent as ActionCreateIcon } from '../../../icons/action-create.svg';
import {
  getActionLabel,
  getActionKeyLabel,
  SPENDING_ACTION_KEYS,
  getQuestionSpendApproval,
} from '../../../lib/desiredActionFields';
import SpendingApprovalModal, { CardSelection } from './SpendingApprovalModal';
import { isOrganisationCard } from './organisationSpend';
import { artifactCheckoutResume } from '../HostedCheckoutArtifactResume';
import { ReplyToItem } from '../../../contexts/ReplyToContext';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import ConfirmationSpecialView, {
  hasSpecialView,
} from '../../ConfirmationSpecialView';
import './yes-or-no-full-screen.scss';
import './multiple-choice-full-screen.scss';
import './response-artifact-full-screen.scss';

const ACTION_ICONS: Record<
  DesiredActionKey,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  buy: ActionBuyIcon,
  reserve: ActionReserveIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionEmailSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarInviteIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: ActionBringAndBuyIcon,
  act: ActionActIcon,
  buy_and_act: ActionBuyAndActIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  other: ActionOtherIcon,
  enhance: ActionBuyIcon,
  find_more: ActionResearchIcon,
  create: ActionCreateIcon,
};

interface YesOrNoFullScreenProps {
  artifact: YesOrNoArtifact;
  selectedChoices: string[];
  onChoiceSelect: (choiceId: string) => void;
  onSubmit: () => void;
  onSubmitWithAction: (
    desiredAction: DesiredActionResponse,
    mandateItems?: Array<{
      actionLine: string;
      descriptionLine: string;
      price: number;
      price_currency: string;
      isDeposit?: boolean;
    }>,
    cardSelection?: CardSelection,
  ) => void | Promise<void>;
  onAcknowledge: () => void;
  onClose: () => void;
  submitting: boolean;
  readOnly?: boolean;
  colorVariants?: {
    base: string;
    midDarker: string;
    darker: string;
    lighter: string;
  };
  pendingSpendItems: Array<{
    id: string;
    actionLine: string;
    descriptionLine: string;
    price: number;
    price_currency: string;
    purchaseSummary?: string;
    purchaseLink?: string;
    delivery?: string;
    deliveryAddress?: string;
    isDeposit?: boolean;
  }>;
  isConfirmationLinked: boolean;
  confirmationMethodName?: string;
  confirmationMethodInput?: any;
  onReject?: () => void;
  onApproveConfirmation?: () => void;
  onChatAbout?: () => void;
  chatButtonText: string;
  agentName?: string;
  agentImageUrl?: string;
  connectedEmails?: string[];
  shareToken?: string;
  agentWhatsappNumber?: string;
}

const FROM_EMAIL_FIELD_KEY = 'from_email_address';

const YesOrNoFullScreen: React.FC<YesOrNoFullScreenProps> = ({
  artifact,
  selectedChoices,
  onChoiceSelect,
  onSubmit,
  onSubmitWithAction,
  onAcknowledge,
  onClose,
  submitting,
  readOnly = false,
  colorVariants,
  pendingSpendItems,
  isConfirmationLinked,
  confirmationMethodName,
  confirmationMethodInput,
  onReject,
  onApproveConfirmation,
  onChatAbout,
  chatButtonText,
  agentName,
  agentImageUrl,
  connectedEmails = [],
  shareToken,
  agentWhatsappNumber,
}) => {
  const hasSelection = selectedChoices.length > 0;
  const hasConfirmationSpecialView =
    !!confirmationMethodName && hasSpecialView(confirmationMethodName);

  const { isMobile } = useMobileNav();

  // Action dropdown state
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const actionDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSheetRef = useRef<HTMLDivElement>(null);
  const actionSectionRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(e.target as Node) &&
        !mobileSheetRef.current?.contains(e.target as Node)
      ) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get desired actions for the currently selected option
  const desiredActions = useMemo(() => {
    const selection = selectedChoices[0];
    if (!selection) return [] as DesiredAction[];
    const actions: DesiredAction[] =
      selection === 'yes'
        ? artifact.yesDesiredActions || []
        : artifact.noDesiredActions || [];
    return [...actions].sort((x, y) => {
      if (x.key === 'other') return 1;
      if (y.key === 'other') return -1;
      return 0;
    });
  }, [selectedChoices, artifact.yesDesiredActions, artifact.noDesiredActions]);

  const hasDesiredActions = desiredActions.length > 0;

  // Find the default action
  const defaultActionKey = useMemo(() => {
    const defaultAction = desiredActions.find((a) => a.default);
    return defaultAction?.key || desiredActions[0]?.key || null;
  }, [desiredActions]);

  // Action selection state
  const [selectedActionKey, setSelectedActionKey] = useState<string | null>(
    null,
  );
  const [questionAnswers, setQuestionAnswers] = useState<
    Record<string, string>
  >({});
  const [otherText, setOtherText] = useState('');
  const [showSpendApproval, setShowSpendApproval] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  // Radius checking for address fields
  const {
    radiusChecking,
    radiusResults,
    checkRadiusForField,
    hasFieldOutsideRadius: checkFieldsOutsideRadius,
  } = useRadiusCheck();

  // Reset action selection when desired actions change (i.e. when user switches yes/no)
  useEffect(() => {
    setSelectedActionKey(defaultActionKey);
    setOtherText('');
    // Pre-fill default values
    const defaultAction = desiredActions.find(
      (a) => a.key === defaultActionKey,
    );
    const prefills: Record<string, string> = {};
    for (const q of defaultAction?.clarifyingQuestions || []) {
      if (q.defaultValue) prefills[q.fieldKey] = q.defaultValue;
    }
    setQuestionAnswers(prefills);
  }, [desiredActions, defaultActionKey]);

  const handleActionChange = useCallback(
    (key: string) => {
      setSelectedActionKey(key);
      const action = desiredActions.find((a) => a.key === key);
      const prefills: Record<string, string> = {};
      for (const q of action?.clarifyingQuestions || []) {
        if (q.defaultValue) prefills[q.fieldKey] = q.defaultValue;
      }
      setQuestionAnswers(prefills);
      setOtherText('');
    },
    [desiredActions],
  );

  const selectedAction = useMemo(
    () => desiredActions.find((a) => a.key === selectedActionKey) || null,
    [desiredActions, selectedActionKey],
  );

  const questions: AnalyzeTodoClarifyingQuestion[] = useMemo(
    () => selectedAction?.clarifyingQuestions || [],
    [selectedAction],
  );

  const fieldsOutsideRadius = useMemo(
    () => checkFieldsOutsideRadius(questions, questionAnswers),
    [checkFieldsOutsideRadius, questions, questionAnswers],
  );

  // Determine spend approval from clarifying questions
  const questionSpendApproval = useMemo(() => {
    if (pendingSpendItems.length > 0) return null;
    if (!selectedAction) return null;
    return getQuestionSpendApproval(selectedAction, questionAnswers);
  }, [selectedAction, pendingSpendItems, questionAnswers]);

  const spendApprovalItems: ReplyToItem[] = useMemo(() => {
    if (
      pendingSpendItems.length > 0 &&
      selectedActionKey &&
      SPENDING_ACTION_KEYS.has(selectedActionKey as DesiredActionKey)
    ) {
      return pendingSpendItems.map((item) => ({
        id: item.id,
        actionLine: item.actionLine,
        descriptionLine: item.descriptionLine,
        bookable: true,
        price: item.price,
        price_currency: item.price_currency,
        purchaseLink: item.purchaseLink,
        delivery: item.delivery,
        deliveryAddress: item.deliveryAddress,
      }));
    }
    if (questionSpendApproval && selectedAction) {
      return [
        {
          id: 'yn-spend',
          actionLine: selectedAction.label || selectedAction.key,
          descriptionLine: questionSpendApproval.description,
          bookable: true,
          price: questionSpendApproval.amount,
          price_currency: questionSpendApproval.currency,
          isDeposit: questionSpendApproval.isDeposit,
        },
      ];
    }
    return [];
  }, [
    pendingSpendItems,
    selectedActionKey,
    questionSpendApproval,
    selectedAction,
  ]);

  const spendIsDeposit = useMemo(
    () => spendApprovalItems.some((item) => item.isDeposit),
    [spendApprovalItems],
  );

  const hasUnansweredRequired = useMemo(
    () =>
      questions.some((q) => q.required && !questionAnswers[q.fieldKey]?.trim()),
    [questions, questionAnswers],
  );

  const isConfirmDisabled =
    submitting ||
    (hasDesiredActions && !selectedActionKey) ||
    (selectedActionKey === 'other' && !otherText.trim()) ||
    hasUnansweredRequired ||
    fieldsOutsideRadius;

  const buildDesiredAction = useCallback((): DesiredActionResponse | null => {
    if (!selectedActionKey) return null;
    if (selectedActionKey === 'other') {
      return { key: 'other' as DesiredActionKey, customText: otherText };
    }
    return {
      key: selectedActionKey as DesiredActionKey,
      questionAnswers:
        Object.keys(questionAnswers).length > 0 ? questionAnswers : undefined,
      label: selectedAction?.label,
    };
  }, [selectedActionKey, otherText, questionAnswers, selectedAction]);

  const handleConfirm = () => {
    if (isConfirmationLinked && selectedChoices.includes('yes')) {
      onApproveConfirmation?.();
      return;
    }
    if (isConfirmationLinked && selectedChoices.includes('no')) {
      onReject?.();
      return;
    }

    if (hasDesiredActions) {
      const action = buildDesiredAction();
      if (!action) return;

      if (spendApprovalItems.length > 0) {
        setShowSpendApproval(true);
        return;
      }

      onSubmitWithAction(action);
    } else {
      onSubmit();
    }
  };

  const handleSpendApprove = useCallback(
    async (cardSel?: CardSelection) => {
      const action = buildDesiredAction();
      if (!action) return;
      const keepSheetOpen = isOrganisationCard(cardSel);
      if (!keepSheetOpen) setShowSpendApproval(false);
      const mandateItems = spendApprovalItems
        .filter(
          (item) => item.bookable && item.price != null && item.price_currency,
        )
        .map((item) => ({
          actionLine: item.actionLine,
          descriptionLine: item.descriptionLine,
          price: item.price!,
          price_currency: item.price_currency!,
          isDeposit: item.isDeposit,
        }));
      await onSubmitWithAction(
        action,
        mandateItems.length > 0 ? mandateItems : undefined,
        cardSel,
      );
      if (keepSheetOpen) setShowSpendApproval(false);
    },
    [buildDesiredAction, spendApprovalItems, onSubmitWithAction],
  );

  const handleSpendCancel = useCallback(() => {
    setShowSpendApproval(false);
  }, []);

  const getConfirmButtonText = () => {
    if (isConfirmationLinked) {
      if (selectedChoices.includes('yes')) return 'Approve';
      if (selectedChoices.includes('no')) return 'Reject';
    }
    return 'Confirm';
  };

  // Scroll to action section when selection changes
  useEffect(() => {
    if (hasSelection && hasDesiredActions && actionSectionRef.current) {
      setTimeout(() => {
        actionSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [hasSelection, hasDesiredActions]);

  return (
    <div className='yn-full-screen response-artifact-full-screen'>
      <div
        className='yn-fs-scroll-area'
        onScroll={(e) => setNavScrolled(e.currentTarget.scrollTop > 0)}
      >
        <UniNav
          mode='artifact'
          scrolled={navScrolled}
          agentWhatsappNumber={agentWhatsappNumber}
          isPublic={!!shareToken}
        />

        {/* Hero header */}
        <div className='yn-fs-hero'>
          <h1 className='yn-fs-title'>{artifact.title || 'Action required'}</h1>
        </div>

        {/* Content */}
        <div className='yn-fs-content'>
          {artifact.overview && (
            <div className='yn-fs-overview'>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {artifact.overview}
              </ReactMarkdown>
            </div>
          )}

          {/* Special confirmation view (e.g. mandate) */}
          {hasConfirmationSpecialView && (
            <div className='yn-fs-special-view'>
              <ConfirmationSpecialView
                methodName={confirmationMethodName!}
                methodInput={confirmationMethodInput}
              />
            </div>
          )}

          {/* Two-column layout */}
          <div className='yn-fs-columns'>
            {/* Left column: yes/no options */}
            <div className='yn-fs-choices'>
              <div
                className={`yn-fs-choice-item ${selectedChoices.includes('yes') ? 'selected' : ''} ${readOnly ? 'read-only' : ''}`}
                onClick={() => !readOnly && onChoiceSelect('yes')}
                role='button'
                tabIndex={0}
                style={
                  selectedChoices.includes('yes') && colorVariants
                    ? {
                        backgroundColor: colorVariants.lighter,
                        borderColor: colorVariants.darker,
                      }
                    : undefined
                }
              >
                <div
                  className={`yn-fs-choice-tick ${selectedChoices.includes('yes') ? 'selected' : ''}`}
                  style={
                    selectedChoices.includes('yes') && colorVariants
                      ? {
                          backgroundColor: colorVariants.darker,
                          borderColor: colorVariants.darker,
                        }
                      : undefined
                  }
                >
                  <TickSelectIcon />
                </div>
                <div className='yn-fs-choice-text'>
                  <b>Yes</b>
                  {artifact.yesBlurb && (
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {artifact.yesBlurb}
                    </ReactMarkdown>
                  )}
                </div>
              </div>

              <div
                className={`yn-fs-choice-item ${selectedChoices.includes('no') ? 'selected' : ''} ${readOnly ? 'read-only' : ''}`}
                onClick={() => !readOnly && onChoiceSelect('no')}
                role='button'
                tabIndex={0}
                style={
                  selectedChoices.includes('no') && colorVariants
                    ? {
                        backgroundColor: colorVariants.lighter,
                        borderColor: colorVariants.darker,
                      }
                    : undefined
                }
              >
                <div
                  className={`yn-fs-choice-tick ${selectedChoices.includes('no') ? 'selected' : ''}`}
                  style={
                    selectedChoices.includes('no') && colorVariants
                      ? {
                          backgroundColor: colorVariants.darker,
                          borderColor: colorVariants.darker,
                        }
                      : undefined
                  }
                >
                  <TickSelectIcon />
                </div>
                <div className='yn-fs-choice-text'>
                  <b>No</b>
                  {artifact.noBlurb && (
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {artifact.noBlurb}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            </div>

            {/* Right column: sidebar */}
            {!readOnly && (
              <div className='yn-fs-sidebar' ref={actionSectionRef}>
                <div
                  className={`yn-fs-sidebar-inner ${hasSelection ? 'has-selection' : ''}`}
                >
                  {hasSelection && hasDesiredActions && (
                    <div className='yn-fs-next-steps-area'>
                      {/* Action dropdown */}
                      <div className='yn-fs-section yn-fs-nextsteps'>
                        <div className='yn-fs-section-title'>Next steps</div>
                        <div
                          className='yn-fs-dropdown-wrapper'
                          ref={actionDropdownRef}
                        >
                          <button
                            className={`yn-fs-action-btn-trigger${actionDropdownOpen ? ' active' : ''}`}
                            onClick={() =>
                              setActionDropdownOpen(!actionDropdownOpen)
                            }
                            disabled={submitting}
                          >
                            {selectedAction &&
                              ACTION_ICONS[
                                selectedAction.key as DesiredActionKey
                              ] &&
                              React.createElement(
                                ACTION_ICONS[
                                  selectedAction.key as DesiredActionKey
                                ],
                              )}
                            {selectedAction
                              ? getActionKeyLabel(selectedAction.key, agentName)
                              : 'Select action'}
                            <ChevronDownIcon className='chevron-icon' />
                          </button>
                          {actionDropdownOpen && !isMobile && (
                            <div className='yn-fs-dropdown-menu'>
                              {desiredActions.map((action) => (
                                <button
                                  key={action.key}
                                  className={`yn-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                  onClick={() => {
                                    handleActionChange(action.key);
                                    setActionDropdownOpen(false);
                                  }}
                                >
                                  {ACTION_ICONS[
                                    action.key as DesiredActionKey
                                  ] &&
                                    React.createElement(
                                      ACTION_ICONS[
                                        action.key as DesiredActionKey
                                      ],
                                    )}
                                  <div className='yn-fs-dropdown-item-text'>
                                    <span className='yn-fs-dropdown-item-title'>
                                      {getActionKeyLabel(action.key, agentName)}
                                    </span>
                                    <span className='yn-fs-dropdown-item-subtitle'>
                                      {getActionLabel(action)}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          {actionDropdownOpen &&
                            isMobile &&
                            createPortal(
                              <div
                                className='mc-fs-dropdown-sheet-backdrop'
                                onClick={() => setActionDropdownOpen(false)}
                              >
                                <div
                                  className='mc-fs-dropdown-sheet'
                                  ref={mobileSheetRef}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className='mc-fs-dropdown-sheet-header'>
                                    What would you like to do?
                                  </div>
                                  {desiredActions.map((action) => (
                                    <button
                                      key={action.key}
                                      className={`mc-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                      onClick={() => {
                                        handleActionChange(action.key);
                                        setActionDropdownOpen(false);
                                      }}
                                    >
                                      {ACTION_ICONS[
                                        action.key as DesiredActionKey
                                      ] &&
                                        React.createElement(
                                          ACTION_ICONS[
                                            action.key as DesiredActionKey
                                          ],
                                        )}
                                      <div className='mc-fs-dropdown-item-text'>
                                        <span className='mc-fs-dropdown-item-title'>
                                          {getActionKeyLabel(
                                            action.key,
                                            agentName,
                                          )}
                                        </span>
                                        <span className='mc-fs-dropdown-item-subtitle'>
                                          {getActionLabel(action)}
                                        </span>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>,
                              document.body,
                            )}
                        </div>
                      </div>

                      {/* Clarifying questions */}
                      {selectedActionKey !== 'other' &&
                        questions.length > 0 && (
                          <div className='yn-fs-questions'>
                            {questions.map((q) => {
                              const isCheckingRadius =
                                radiusChecking[q.fieldKey];
                              const isOutsideRadius =
                                q.mustBeWithinRadius &&
                                questionAnswers[q.fieldKey] &&
                                radiusResults[q.fieldKey] === false;

                              return (
                                <div
                                  key={q.fieldKey}
                                  className={`yn-fs-question${isOutsideRadius ? ' outside-radius' : ''}`}
                                >
                                  <label>
                                    {q.label}
                                    {q.required && (
                                      <span className='yn-fs-required'>*</span>
                                    )}
                                  </label>
                                  {q.type === 'select' ? (
                                    <select
                                      value={questionAnswers[q.fieldKey] || ''}
                                      onChange={(e) =>
                                        setQuestionAnswers((prev) => ({
                                          ...prev,
                                          [q.fieldKey]: e.target.value,
                                        }))
                                      }
                                    >
                                      <option value=''>Select...</option>
                                      {(q.fieldKey === FROM_EMAIL_FIELD_KEY &&
                                      q.type === 'select' &&
                                      connectedEmails.length > 0
                                        ? connectedEmails
                                        : q.options || []
                                      ).map((opt) => (
                                        <option key={opt} value={opt}>
                                          {opt}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className='yn-fs-input-wrapper'>
                                      {q.type === 'number' && q.currency && (
                                        <span className='yn-fs-currency-prefix'>
                                          {getSymbolFromCurrency(q.currency) ||
                                            q.currency}
                                        </span>
                                      )}
                                      {q.type === 'textarea' ? (
                                        <textarea
                                          className='yn-fs-textarea'
                                          value={
                                            questionAnswers[q.fieldKey] || ''
                                          }
                                          onChange={(e) =>
                                            setQuestionAnswers((prev) => ({
                                              ...prev,
                                              [q.fieldKey]: e.target.value,
                                            }))
                                          }
                                          placeholder=''
                                          rows={3}
                                        />
                                      ) : (
                                        <input
                                          type={
                                            q.type === 'number'
                                              ? 'number'
                                              : 'text'
                                          }
                                          value={
                                            questionAnswers[q.fieldKey] || ''
                                          }
                                          onChange={(e) =>
                                            setQuestionAnswers((prev) => ({
                                              ...prev,
                                              [q.fieldKey]: e.target.value,
                                            }))
                                          }
                                          onBlur={() => {
                                            if (
                                              q.mustBeWithinRadius &&
                                              questionAnswers[q.fieldKey]
                                            ) {
                                              checkRadiusForField(
                                                q.fieldKey,
                                                questionAnswers[q.fieldKey],
                                              );
                                            }
                                          }}
                                          placeholder=''
                                        />
                                      )}
                                    </div>
                                  )}
                                  {isCheckingRadius && (
                                    <span className='yn-fs-field-hint checking'>
                                      Checking area...
                                    </span>
                                  )}
                                  {isOutsideRadius && (
                                    <span className='yn-fs-field-hint error'>
                                      Outside Dreamster operating area
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                      {/* Other text input */}
                      {selectedActionKey === 'other' && (
                        <div className='yn-fs-questions'>
                          <div className='yn-fs-question'>
                            <label>
                              What would you like done?
                              <span className='yn-fs-required'>*</span>
                            </label>
                            <textarea
                              className='yn-fs-textarea'
                              value={otherText}
                              onChange={(e) => setOtherText(e.target.value)}
                              placeholder=''
                              rows={3}
                            />
                          </div>
                        </div>
                      )}

                      {/* Confirm button */}
                      {!showSpendApproval && (
                        <div className='yn-fs-confirm-wrapper'>
                          {selectedAction && (
                            <p className='yn-fs-action-label'>
                              {getActionLabel(selectedAction)}
                            </p>
                          )}
                          <button
                            className='yn-fs-action-btn'
                            onClick={handleConfirm}
                            disabled={isConfirmDisabled}
                          >
                            {getConfirmButtonText()}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Simple confirm when no desired actions */}
                  {hasSelection && !hasDesiredActions && (
                    <div className='yn-fs-action-area'>
                      <div className='yn-fs-confirm-wrapper'>
                        <button
                          className='yn-fs-action-btn'
                          onClick={handleConfirm}
                          disabled={submitting}
                        >
                          {getConfirmButtonText()}
                        </button>
                      </div>
                    </div>
                  )}

                  <SpendingApprovalModal
                    show={showSpendApproval}
                    onApprove={handleSpendApprove}
                    onCancel={handleSpendCancel}
                    items={spendApprovalItems}
                    agentImageUrl={agentImageUrl}
                    assistantName={agentName || 'Secretary'}
                    isDeposit={spendIsDeposit}
                    actionKey={selectedActionKey ?? undefined}
                    shareToken={shareToken}
                    checkoutResume={artifactCheckoutResume(artifact.id)}
                  />

                  {hasSelection && onChatAbout && (
                    <button className='yn-fs-chat-btn' onClick={onChatAbout}>
                      <SpeechBubbleIcon />
                      {chatButtonText}
                    </button>
                  )}

                  {!hasSelection && (
                    <button
                      className='yn-fs-remove-btn'
                      onClick={onAcknowledge}
                      disabled={submitting}
                    >
                      <MinusCircleIcon /> Remove task
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YesOrNoFullScreen;
